import type { RealtimeVoiceFeatureSettings, RealtimeVoiceFeatureSettingsWrite, DesktopRealtimeVoiceAvailability, DesktopVoiceAvailability } from '../../shared-types/src/realtime.js';
import type { RealtimeVoiceAdminView, RealtimeVoiceAdminWrite, TenantVoiceAdminView, TenantVoicePolicyWrite, VoiceFeatureSettings, VoiceFeatureSettingsWrite, VoiceModelAdminWrite } from "../../shared-types/src/realtime.js";
import type { SessionProtocol, SessionCapabilities, SessionRevokeInput, RotatingTokenPair } from '../../shared-types/src/session.js';
import type { RecommendedModelProfileWrite } from '../../shared-types/src/recommended-models.js';
import type { ModelThinkingBootstrapOptions } from '../../shared-types/src/model-thinking.js';
// In-process `ServiceClient` implementation that wraps a `ServiceApi`
// instance (the service-api core). Used by:
//   - the desktop main process (so the embedded service-api and the desktop
//     speak through one typed surface, never raw `new ServiceApi(...)`),
//   - tests, where spinning up a real HTTP server is unnecessary.
//
// The `getAccessToken` is supplied by the caller (typically the desktop
// account manager, which owns the short-lived service tokens). The
// in-process client trusts the token to do identity checks the same way
// the HTTP transport would, but the calls go through `ServiceApi` methods
// directly — no JSON serialisation in the middle.

import type {
  AdminAuditEvent,
  CapabilitySourceAssignment,
  AdminGroupView,
  AdminOverview,
  AdminUserView,
  AutoRoutingPreviewRequest,
  AutoRoutingPreviewResult,
  AutoRoutingDecisionsView,
  AutoRoutingProfileView,
  AutoRoutingProfileWrite,
  AgentPermissionMode,
  AuditListQuery,
  DesktopBootstrap,
  DiagnosticBundleListQuery,
  DiagnosticBundleView,
  RequiredConsent,
  DesktopModelView,
  DesktopPlatform,
  DownloadChannel,
  DownloadManifest,
  ExtensionManifest,
  GatewayBackend,
  GatewayCompatibility,
  Group,
  InvitedUser,
  Announcement,
  AnnouncementListQuery,
  CreateAnnouncementRequest,
  MarkAllAnnouncementsReadResult,
  MeAnnouncementsResponse,
  PatchAnnouncementRequest,
  ListQuery,
  ModelRoute,
  ModelRoutingPolicy,
  OrganizationSsoConnectionAdmin,
  OrganizationSsoConnectionPatch,
  OrganizationSsoConnectionTestInput,
  OrganizationSsoConnectionTestResult,
  OrganizationSsoConnectionWrite,
  OrganizationSsoDiscovery,
  OrganizationSsoIdentifierType,
  OrganizationSsoMode,
  AllocationOverview,
  CreditAllocation,
  CreditLimitView,
  EffectiveCreditLimitsRequest,
  EffectiveCreditLimitsView,
  UpsertCreditLimitRequest,
  CreateQuotaProfileRequest,
  MembershipQuotaAssignment,
  QuotaProfileAssignmentPreview,
  QuotaProfileLimitInput,
  QuotaProfileListView,
  QuotaProfileView,
  UpdateQuotaProfileRequest,
  UserQuotaAssignmentQuery,
  UserQuotaAssignmentView,
  Paginated,
  Partner,
  PartnerAdminView,
  PartnerOverview,
  PlatformAdminView,
  PartnerWebSearchUsageSummary,
  PasswordLoginInput,
  PasswordResetCodeAccepted,
  PasswordResetCodeConfirmInput,
  PasswordResetCodeRequestInput,
  PasswordResetCodeResendInput,
  PasswordResetCodeResent,
  PasswordResetCodeVerifyInput,
  PasswordResetCodeVerified,
  PlatformProviderKeyProvider,
  PlatformProviderModelDefinition,
  PlatformProviderModelDeployment,
  PlatformProviderModelListQuery,
  PlatformProviderModelTestResult,
  CreatePlatformProviderModelRequest,
  DeployPlatformProviderModelRequest,
  DeployPlatformProviderModelResult,
  BulkDeployPlatformProviderModelsRequest,
  BulkDeployPlatformProviderModelsResult,
  PublishPlatformProviderModelRatesRequest,
  PublishPlatformProviderModelRatesResult,
  UpdatePlatformProviderModelRequest,
  PlatformWebSearchKeySummary,
  ProviderKeyRegistrationResult,
  ProviderKeyRemovalResult,
  ProviderKeySummary,
  ProviderKeyTestResult,
  TestLiteLLMConnectionInput,
  TestProviderKeyInput,
  PolicyContextSnapshot,
  QuotaPolicy,
  ServiceRole,
  SocialLoginMode,
  SocialProviderConfig,
  SocialProviderKind,
  SocialProviderPublic,
  SignupConfig,
  SignupAccepted,
  SignupInput,
  Tenant,
  TenantPartnerTransferResult,
  TenantSelfSignupResult,
  TenantOrganizationSsoConfig,
  TenantAssignment,
  TenantLoginDefaultModelView,
  TenantMcpConnectors,
  TenantMediaRoute,
  TenantMediaRouteSummary,
  TenantModelAccess,
  TenantSocialConfig,
  TenantSocialProvider,
  TenantWebSearchBackend,
  TenantWebSearchBackendSummary,
  DeploymentProfile,
  DefaultSignupTenantSetting,
  CreditRateTableView,
  CreditRateSettingsView,
  PutCreditRateSettingsRequest,
  PutCreditRateSettingsResult,
  MemberQuotaDefault,
  UsageChainVerification,
  UsageEventView,
  UsageQuery,
  CreditSettlementQuery,
  CreditSettlementReport,
  UsageSeriesPoint,
  UsageSeriesQuery,
  UsageSummaryView,
  UserListQuery,
  VelugaModelCatalogItem,
  WebSearchNativeProvider,
  CapabilitySourceRecord,
  ExtensionReleaseView,
  BillingHistoryPage,
  CardRegistrationStart,
  CardRegistrationView,
  CreateCreditOrderInput,
  CreditPurchaseOrderView,
  MyBillingSummary,
  ResolvedCreditOffer,
} from '../../shared-types/src/index.js';
import type { MergePolicyInput } from '../../policy-service/src/merge.js';
import type { AdminService } from '../../service-api/src/admin.js';
import type { Actor } from '../../service-api/src/admin.js';
import {
  MemoryQuotaStore,
  type QuotaPersistence,
} from '../../service-api/src/quota-store.js';
import type { ServiceApi } from '../../service-api/src/service.js';
import type { PersonalCreditPurchaseService } from '../../service-api/src/billing/purchase-service.js';
import type { BillingControlPlaneService } from '../../service-api/src/billing/control-plane-service.js';
import type { PersonalCardService } from '../../service-api/src/billing/card-service.js';
import type { ServiceTokenClaims } from '../../service-api/src/tokens.js';
import type { DiagnosticBundleMeta, DiagnosticsIntakeApi } from '../../service-api/src/diagnostics/intake.js';
import { DIAGNOSTICS_SIGNED_URL_TTL_SECONDS } from '../../service-api/src/diagnostics/storage.js';
import { ServiceError } from '../../service-api/src/errors.js';
import type {
  CreatedUserResult,
  GatewayBackendAdminView,
  AllocationBatchInput,
  AllocationBatchResult,
  AllocationInput,
  LoginStartResult,
  ModelAccessSubject,
  ModelRouteAdminView,
  PartnerCustomModelDeployment,
  PartnerCustomModelSummary,
  PartnerCustomModelTestRequest,
  UpdatePartnerCustomModelRequest,
  UpdatePartnerCustomModelResult,
  PartnerCustomModelTestResult,
  PartnerCustomModelWrite,
  CapabilitySourceRegisterInput,
  MemberQuotaDefaultInput,
  ServiceClient,
  TokenPair,
  AccessTokenResult,
  ActAsSessionResult,
} from './index.js';
import { createInProcessDownloads } from './in-process-downloads.js';

export interface InProcessServiceClientDeps {
  api: ServiceApi;
  admin: AdminService;
  quotaPolicies?: QuotaPersistence;
  diagnosticsIntake?: DiagnosticsIntakeApi;
  purchaseService?: PersonalCreditPurchaseService;
  billingService?: BillingControlPlaneService;
  cardService?: PersonalCardService;
}

export class InProcessServiceClient implements ServiceClient {
  private readonly quotaPolicies: QuotaPersistence;
  private readonly downloads = createInProcessDownloads({
    api: () => this.deps.api,
    verify: async (accessToken) => (await this.verify(accessToken)),
  });

  constructor(private readonly deps: InProcessServiceClientDeps) {
    this.quotaPolicies = deps.quotaPolicies ?? new MemoryQuotaStore();

  }

  // ── auth ────────────────────────────────────────────────────────────

  sessionCapabilities(): Promise<SessionCapabilities> { return this.deps.api.sessionCapabilities(); }
  refreshSession(refreshToken: string): Promise<RotatingTokenPair> { return this.deps.api.refreshSession(refreshToken); }
  revokeSession(input: SessionRevokeInput): Promise<{ ok: true }> { return this.deps.api.revokeSession(input); }

  loginStart(
    invite?: { tenantId: string; userId: string; inviteToken: string; sessionProtocol?: SessionProtocol },
    opts?: { providerId?: string; sessionProtocol?: SessionProtocol }
  ): Promise<LoginStartResult> {
    return this.deps.api.loginStart(invite, opts);
  }

  listSocialProviders(tenantHint?: string): Promise<SocialProviderPublic[]> {
    return this.deps.api.listSocialProviders(tenantHint);
  }
  getSignupConfig(): Promise<SignupConfig> {
    return this.deps.api.getSignupConfig();
  }


  signup(input: SignupInput): Promise<SignupAccepted> {
    return this.deps.api.signupWithEmail(input);
  }

  listOrganizationSsoConnections(
    tenantHint?: string
  ): Promise<OrganizationSsoDiscovery> {
    return this.deps.api.listOrganizationSsoConnections(tenantHint);
  }

  organizationSsoLoginStart(input: {
    sessionProtocol?: SessionProtocol;
    connectionId: string;
    identifierType?: OrganizationSsoIdentifierType;
    identifier?: string;
  }): Promise<LoginStartResult> {
    return this.deps.api.organizationSsoLoginStart(input);
  }

  loginComplete(
    input: string | { tenantId: string; userId: string; inviteToken: string; sessionProtocol?: SessionProtocol }
  ): Promise<TokenPair> {
    // The public `ServiceApi.loginComplete` overloads take either a
    // `loginHandle` (string) or an `InviteLoginInput` (object). TypeScript
    // can't narrow the union through a single call site, so we branch on
    // `typeof` to land on the matching overload. Sync throws (e.g. when
    // the handle was never authorized) are converted to a rejected Promise
    // so callers can use `await … .rejects.toThrow(...)` consistently with
    // the HTTP transport.
    try {
      return typeof input === 'string'
        ? this.deps.api.loginComplete(input)
        : this.deps.api.loginComplete(input);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  loginWithPassword(input: PasswordLoginInput): Promise<TokenPair> {
    return this.deps.api.loginWithPassword(input);
  }

  changePassword(
    accessToken: string,
    oldPassword: string,
    newPassword: string
  ): Promise<TokenPair | void> {
    return this.deps.api.changePassword(accessToken, oldPassword, newPassword);
  }

  keepPassword(accessToken: string): Promise<void> {
    return this.deps.api.keepPassword(accessToken);
  }

  refresh(refreshToken: string): Promise<AccessTokenResult> {
    return Promise.resolve(this.deps.api.refresh(refreshToken));
  }

  // ── self / desktop bootstrap ────────────────────────────────────────

  bootstrap(accessToken: string, options?: ModelThinkingBootstrapOptions): Promise<DesktopBootstrap> {
    return this.deps.api.bootstrap(accessToken, options);
  }
  async getMyBillingSummary(accessToken: string): Promise<MyBillingSummary> {
    const { tenantId, userId } = (await this.verify(accessToken));
    if (!this.deps.billingService) {
      return {
        accountScope: `${tenantId}:${userId}`,
        purchaseEnabled: false,
        baseCreditMillis: 0,
        personalCreditMillis: 0,
        paymentMethod: null,
      };
    }
    const summary = await this.deps.billingService.getMySummary(
      tenantId,
      userId,
      await this.deps.api.billingBaseCreditMillis(tenantId, userId)
    );
    return {
      ...summary,
      paymentMethod: this.deps.cardService
        ? await this.deps.cardService.getMyPaymentMethod({ tenantId, userId })
        : null,
      purchaseEnabled:
        summary.purchaseEnabled && this.deps.api.personalCreditPurchaseEnabled(),
    };
  }
  async listMyCreditProducts(accessToken: string): Promise<ResolvedCreditOffer[]> {
    const { tenantId } = (await this.verify(accessToken));
    if (!this.deps.billingService || !this.deps.api.personalCreditPurchaseEnabled()) {
      return [];
    }
    return this.deps.billingService.listMyProducts(tenantId);
  }
  async startCardRegistration(accessToken: string): Promise<CardRegistrationStart> {
    const actor = (await this.verify(accessToken));
    if (!this.deps.cardService)
      return Promise.reject(new Error('billing_purchase_service_unavailable'));
    return this.deps.cardService.startCardRegistration(actor);
  }
  async getCardRegistration(
    accessToken: string,
    registrationId: string
  ): Promise<CardRegistrationView> {
    const actor = (await this.verify(accessToken));
    if (!this.deps.cardService)
      return Promise.reject(new Error('billing_purchase_service_unavailable'));
    return this.deps.cardService.getCardRegistration(actor, registrationId);
  }
  async deleteMyCard(accessToken: string): Promise<void> {
    const actor = (await this.verify(accessToken));
    if (!this.deps.cardService)
      return Promise.reject(new Error('billing_purchase_service_unavailable'));
    return this.deps.cardService.deleteMyCard(actor);
  }
  async createCreditOrder(
    accessToken: string,
    input: CreateCreditOrderInput
  ): Promise<CreditPurchaseOrderView> {
    const { tenantId, userId } = (await this.verify(accessToken));
    if (!this.deps.purchaseService)
      return Promise.reject(new Error('billing_purchase_service_unavailable'));
    return this.deps.purchaseService.create({ tenantId, userId }, input);
  }
  async payCreditOrder(
    accessToken: string,
    orderId: string
  ): Promise<CreditPurchaseOrderView> {
    const { tenantId, userId } = (await this.verify(accessToken));
    if (!this.deps.purchaseService)
      return Promise.reject(new Error('billing_purchase_service_unavailable'));
    return this.deps.purchaseService.pay({ tenantId, userId }, orderId);
  }
  async getCreditOrder(
    accessToken: string,
    orderId: string
  ): Promise<CreditPurchaseOrderView> {
    const { tenantId, userId } = (await this.verify(accessToken));
    if (!this.deps.purchaseService)
      return Promise.reject(new Error('billing_purchase_service_unavailable'));
    return this.deps.purchaseService.get({ tenantId, userId }, orderId);
  }
  async listMyBillingHistory(
    accessToken: string,
    input: { page: number; pageSize: number }
  ): Promise<BillingHistoryPage> {
    const { tenantId, userId } = (await this.verify(accessToken));
    if (!this.deps.purchaseService)
      return Promise.reject(new Error('billing_purchase_service_unavailable'));
    return this.deps.purchaseService.listHistory({ tenantId, userId }, input);
  }
  async acceptConsents(
    accessToken: string,
    input: { consents: RequiredConsent[] }
  ): Promise<{ pendingConsents: RequiredConsent[] }> {
    const { tenantId, userId } = (await this.verify(accessToken));
    return this.deps.api.acceptConsents(tenantId, userId, input.consents);
  }
  async uploadDiagnosticBundle(
    accessToken: string,
    bytes: Uint8Array,
    meta: DiagnosticBundleMeta
  ): Promise<{ id: string; uploadedAt: string }> {
    if (!this.deps.diagnosticsIntake) {
      return Promise.reject(new ServiceError('diagnostics_storage_unavailable', 503));
    }
    return this.deps.diagnosticsIntake.ingest((await this.verify(accessToken)), bytes, meta);
  }
  async catalog(accessToken: string): Promise<DesktopModelView[]> {
    return this.deps.api.catalog((await this.tenantOf(accessToken)));
  }
  async availableModels(accessToken: string): Promise<DesktopModelView[]> {
    const { tenantId, userId } = (await this.verify(accessToken));
    return this.deps.api.availableModels(tenantId, userId);
  }
  async policiesEffective(accessToken: string): Promise<PolicyContextSnapshot> {
    return (await this.deps.api.bootstrap(accessToken)).effectivePolicy;
  }
  async postClientEvent(
    _accessToken: string,
    _event: Record<string, unknown>
  ): Promise<void> {
    // Append-only audit log lives behind the service-api store; Phase 4
    // binds the desktop client-events stream to it. Until then we accept
    // and drop — the same no-op the in-memory HTTP handler performs.
  }

  // ── downloads ──────────────────────────────────────────────────────

  downloadChannels(accessToken: string): Promise<DownloadChannel[]> {
    return this.downloads.downloadChannels(accessToken);
  }
  downloadLatest(
    accessToken: string,
    channel: DownloadChannel,
    platform: DesktopPlatform
  ): Promise<DownloadManifest> {
    return this.downloads.downloadLatest(accessToken, channel, platform);
  }
  extensionLatest(
    accessToken: string,
    extensionId: string,
    platform: 'win32' | 'darwin',
    request: { appVersion: string; protocolVersion: number }
  ): Promise<ExtensionManifest> {
    return this.downloads.extensionLatest(accessToken, extensionId, platform, request);
  }

  // ── gateway capabilities ───────────────────────────────────────────

  gatewayCapabilities(_accessToken: string): Promise<GatewayCompatibility> {
    return Promise.resolve(this.deps.api.gatewayCapabilities());
  }

  // ── platform ───────────────────────────────────────────────────────

  platformLoginWithPassword(input: {
    sessionProtocol?: SessionProtocol;
    email: string;
    password: string;
  }): Promise<TokenPair> {
    return this.deps.api.loginPlatformWithPassword(input.email, input.password, input.sessionProtocol);
  }

  listTenants(accessToken: string): Promise<Tenant[]>;
  listTenants(
    accessToken: string,
    query: ListQuery
  ): Promise<Paginated<Tenant>>;
  async listTenants(
    accessToken: string,
    query?: ListQuery
  ): Promise<Tenant[] | Paginated<Tenant>> {
    const actor = (await this.actorOf(accessToken));
    return query
      ? this.deps.admin.queryTenants(actor, query)
      : this.deps.admin.listTenants(actor);
  }

  async createTenant(
    accessToken: string,
    input: {
      name: string;
      slug?: string;
      deploymentProfile?: DeploymentProfile;
      partnerId?: string;
      idempotencyKey: string;
    }
  ): Promise<Tenant> {
    return this.deps.admin.createTenant((await this.actorOf(accessToken)), input);
  }

  async deleteTenant(
    accessToken: string,
    input: { tenantId: string; confirmName: string; idempotencyKey: string }
  ): Promise<void> {
    return this.deps.admin.deleteTenant((await this.actorOf(accessToken)), input);
  }

  async transferTenantPartner(
    accessToken: string,
    input: {
      tenantId: string;
      partnerId: string | null;
      confirmName?: string;
      dryRun?: boolean;
      idempotencyKey: string;
    }
  ): Promise<TenantPartnerTransferResult> {
    return this.deps.admin.transferTenantPartner((await this.actorOf(accessToken)), input);
  }

  async actAsTenant(
    accessToken: string,
    tenantId: string
  ): Promise<ActAsSessionResult> {
    // Authorization (platform.act_as.write) is enforced inside issueActAsSession,
    // shared with the HTTP path, so an unauthorized caller fails with the same
    // RbacError here instead of a transport-specific error string.
    return this.deps.api.issueActAsSession((await this.actorOf(accessToken)), tenantId, accessToken);
  }

  async registerProviderKey(
    accessToken: string,
    input: {
      tenantId: string;
      provider: PlatformProviderKeyProvider;
      apiKey: string;
      baseUrl?: string;
      litellmBaseUrl?: string;
      idempotencyKey: string;
    }
  ): Promise<ProviderKeyRegistrationResult> {
    return this.deps.admin.registerProviderKey(
      (await this.platformActorOf(accessToken, input.tenantId)),
      input
    );
  }

  async listPlatformProviderModels(
    accessToken: string,
    query: PlatformProviderModelListQuery = {}
  ): Promise<Paginated<PlatformProviderModelDefinition>> {
    return this.deps.admin.listPlatformProviderModels(
      (await this.platformActorOf(accessToken, '__platform__')),
      query
    );
  }

  async getPlatformProviderModel(
    accessToken: string,
    provider: PlatformProviderKeyProvider,
    modelKey: string
  ): Promise<PlatformProviderModelDefinition> {
    return this.deps.admin.getPlatformProviderModel(
      (await this.platformActorOf(accessToken, '__platform__')),
      provider,
      modelKey
    );
  }

  async createPlatformProviderModel(
    accessToken: string,
    input: CreatePlatformProviderModelRequest & { idempotencyKey: string }
  ): Promise<PlatformProviderModelDefinition> {
    const { idempotencyKey, ...definition } = input;
    return this.deps.admin.createPlatformProviderModel(
      (await this.platformActorOf(accessToken, '__platform__')),
      definition,
      { idempotencyKey }
    );
  }

  async updatePlatformProviderModel(
    accessToken: string,
    provider: PlatformProviderKeyProvider,
    modelKey: string,
    input: UpdatePlatformProviderModelRequest & { idempotencyKey: string }
  ): Promise<PlatformProviderModelDefinition> {
    const { idempotencyKey, ...definition } = input;
    return this.deps.admin.updatePlatformProviderModel(
      (await this.platformActorOf(accessToken, '__platform__')),
      provider,
      modelKey,
      definition,
      { idempotencyKey }
    );
  }

  async disablePlatformProviderModel(
    accessToken: string,
    provider: PlatformProviderKeyProvider,
    modelKey: string,
    idempotencyKey: string
  ): Promise<PlatformProviderModelDefinition> {
    return this.deps.admin.disablePlatformProviderModel(
      (await this.platformActorOf(accessToken, '__platform__')),
      provider,
      modelKey,
      { idempotencyKey }
    );
  }

  async testPlatformProviderModel(
    accessToken: string,
    provider: PlatformProviderKeyProvider,
    modelKey: string,
    tenantId: string
  ): Promise<PlatformProviderModelTestResult> {
    return this.deps.admin.testPlatformProviderModel(
      (await this.platformActorOf(accessToken, '__platform__')),
      provider,
      modelKey,
      tenantId
    );
  }

  async deployPlatformProviderModel(
    accessToken: string,
    provider: PlatformProviderKeyProvider,
    modelKey: string,
    input: DeployPlatformProviderModelRequest & { idempotencyKey: string }
  ): Promise<DeployPlatformProviderModelResult> {
    const { idempotencyKey, ...body } = input;
    return this.deps.admin.deployPlatformProviderModel(
      (await this.platformActorOf(accessToken, '__platform__')),
      provider,
      modelKey,
      body,
      { idempotencyKey }
    );
  }

  async bulkDeployPlatformProviderModels(
    accessToken: string,
    tenantId: string,
    input: BulkDeployPlatformProviderModelsRequest & { idempotencyKey: string }
  ): Promise<BulkDeployPlatformProviderModelsResult> {
    const { idempotencyKey, ...body } = input;
    return this.deps.admin.bulkDeployPlatformProviderModels(
      (await this.platformActorOf(accessToken, '__platform__')),
      tenantId,
      body,
      { idempotencyKey }
    );
  }

  async publishPlatformProviderModelRates(
    accessToken: string,
    provider: PlatformProviderKeyProvider,
    modelKey: string,
    input: PublishPlatformProviderModelRatesRequest & { idempotencyKey: string }
  ): Promise<PublishPlatformProviderModelRatesResult> {
    const { idempotencyKey, ...body } = input;
    return this.deps.admin.publishPlatformProviderModelRates(
      (await this.platformActorOf(accessToken, '__platform__')),
      provider,
      modelKey,
      body,
      { idempotencyKey }
    );
  }

  async revokePlatformProviderModelDeployment(
    accessToken: string,
    provider: PlatformProviderKeyProvider,
    modelKey: string,
    tenantId: string,
    idempotencyKey: string
  ): Promise<PlatformProviderModelDeployment> {
    return this.deps.admin.revokePlatformProviderModelDeployment(
      (await this.platformActorOf(accessToken, '__platform__')),
      provider,
      modelKey,
      tenantId,
      { idempotencyKey }
    );
  }

  async getRealtimeVoiceSettings(accessToken: string): Promise<RealtimeVoiceFeatureSettings> {
    return this.deps.admin.getRealtimeVoiceSettings(await this.actorOf(accessToken));
  }
  async putRealtimeVoiceSettings(accessToken: string, input: RealtimeVoiceFeatureSettingsWrite, idempotencyKey: string): Promise<RealtimeVoiceFeatureSettings> {
    return this.deps.admin.putRealtimeVoiceSettings(await this.actorOf(accessToken), input, idempotencyKey);
  }
  async getMyRealtimeVoice(accessToken: string, options: import('./index.js').VoiceRequestOptions = {}): Promise<DesktopRealtimeVoiceAvailability> {
    const timeout = AbortSignal.timeout(options.timeoutMs ?? 5000);
    const signal = options.signal ? AbortSignal.any([timeout, options.signal]) : timeout;
    signal.throwIfAborted();
    const result = await this.deps.api.getMyRealtimeVoice(accessToken);
    signal.throwIfAborted();
    return result;
  }
  async getMyVoiceAvailability(accessToken: string, options: import('./index.js').VoiceRequestOptions = {}): Promise<DesktopVoiceAvailability> {
    const timeout = AbortSignal.timeout(options.timeoutMs ?? 5000);
    const signal = options.signal ? AbortSignal.any([timeout, options.signal]) : timeout;
    signal.throwIfAborted();
    const result = await this.deps.api.getMyVoiceAvailability(accessToken);
    signal.throwIfAborted();
    return result;
  }

  async getRealtimeVoice(accessToken: string, tenantId: string): Promise<RealtimeVoiceAdminView> {
    const claims = await this.deps.api.verifyActiveToken(accessToken, 'access');
    return this.deps.admin.getRealtimeVoice({ tenantId: claims.tenantId, userId: claims.userId, ...(claims.scope ? { scope: claims.scope } : {}), ...(claims.actAs ? { actAs: claims.actAs } : {}) }, tenantId);
  }
  async putRealtimeVoice(accessToken: string, tenantId: string, input: RealtimeVoiceAdminWrite, idempotencyKey: string): Promise<RealtimeVoiceAdminView> {
    const claims = await this.deps.api.verifyActiveToken(accessToken, 'access');
    return this.deps.admin.putRealtimeVoice({ tenantId: claims.tenantId, userId: claims.userId, ...(claims.scope ? { scope: claims.scope } : {}), ...(claims.actAs ? { actAs: claims.actAs } : {}) }, tenantId, input, idempotencyKey);
  }
  async getVoiceSettings(accessToken: string): Promise<VoiceFeatureSettings> {
    return this.deps.admin.getVoiceSettings(await this.actorOf(accessToken));
  }
  async putVoiceSettings(accessToken: string, input: VoiceFeatureSettingsWrite, idempotencyKey: string): Promise<VoiceFeatureSettings> {
    return this.deps.admin.putVoiceSettings(await this.actorOf(accessToken), input, idempotencyKey);
  }
  async getTenantVoice(accessToken: string, tenantId: string): Promise<TenantVoiceAdminView> {
    return this.deps.admin.getTenantVoice(await this.actorOf(accessToken), tenantId);
  }
  async putTenantVoice(accessToken: string, tenantId: string, input: TenantVoicePolicyWrite, idempotencyKey: string): Promise<TenantVoiceAdminView> {
    return this.deps.admin.putTenantVoice(await this.actorOf(accessToken), tenantId, input, idempotencyKey);
  }
  async postTenantVoiceModel(accessToken: string, tenantId: string, input: VoiceModelAdminWrite, idempotencyKey: string): Promise<TenantVoiceAdminView> {
    return this.deps.admin.postTenantVoiceModel(await this.actorOf(accessToken), tenantId, input, idempotencyKey);
  }

  async getRecommendedModelProfile(accessToken: string, tenantId: string) {
    const claims = await this.deps.api.verifyActiveToken(accessToken, 'access');
    return this.deps.admin.getRecommendedModelProfile({ tenantId: claims.tenantId, userId: claims.userId, ...(claims.scope ? { scope: claims.scope } : {}), ...(claims.actAs ? { actAs: claims.actAs } : {}) }, tenantId);
  }

  async putRecommendedModelProfile(accessToken: string, input: { tenantId: string; profile: RecommendedModelProfileWrite; expectedRevision: number; idempotencyKey: string }) {
    const claims = await this.deps.api.verifyActiveToken(accessToken, 'access');
    return this.deps.admin.putRecommendedModelProfile({ tenantId: claims.tenantId, userId: claims.userId, ...(claims.scope ? { scope: claims.scope } : {}), ...(claims.actAs ? { actAs: claims.actAs } : {}) }, input);
  }

  async getAutoRoutingProfile(accessToken: string, tenantId: string): Promise<AutoRoutingProfileView> {
    return this.deps.admin.getAutoRoutingProfile((await this.platformActorOf(accessToken, tenantId)), tenantId);
  }

  async putAutoRoutingProfile(
    accessToken: string,
    input: { tenantId: string; profile: AutoRoutingProfileWrite; idempotencyKey: string }
  ): Promise<AutoRoutingProfileView> {
    return this.deps.admin.putAutoRoutingProfile((await this.platformActorOf(accessToken, input.tenantId)), input);
  }

  async previewAutoRouting(
    accessToken: string,
    input: { tenantId: string; preview: AutoRoutingPreviewRequest }
  ): Promise<AutoRoutingPreviewResult> {
    return this.deps.admin.previewAutoRouting(
      (await this.platformActorOf(accessToken, input.tenantId)),
      input.tenantId,
      input.preview,
    );
  }

  async listAutoRoutingDecisions(
    accessToken: string,
    tenantId: string,
    limit?: number
  ): Promise<AutoRoutingDecisionsView> {
    return this.deps.admin.listAutoRoutingDecisions(
      (await this.platformActorOf(accessToken, tenantId)),
      tenantId,
      limit === undefined ? {} : { limit }
    );
  }

  async removeProviderKey(
    accessToken: string,
    input: {
      tenantId: string;
      provider: PlatformProviderKeyProvider;
      idempotencyKey: string;
    }
  ): Promise<ProviderKeyRemovalResult> {
    return this.deps.admin.removeProviderKey(
      (await this.platformActorOf(accessToken, input.tenantId)),
      input
    );
  }

  async getProviderKeys(
    accessToken: string,
    tenantId: string
  ): Promise<ProviderKeySummary[]> {
    return this.deps.admin.listProviderKeys(
      (await this.platformActorOf(accessToken, tenantId)),
      tenantId
    );
  }

  async listTenantWebSearchBackends(
    accessToken: string,
    tenantId: string
  ): Promise<TenantWebSearchBackendSummary[]> {
    return this.deps.admin.listTenantWebSearchBackends(
      (await this.platformActorOf(accessToken, tenantId)),
      tenantId
    );
  }

  async listTenantMediaRoutes(accessToken: string, tenantId: string): Promise<TenantMediaRouteSummary[]> {
    return this.deps.admin.listTenantMediaRoutes(
      (await this.platformActorOf(accessToken, tenantId)),
      tenantId
    );
  }

  async getPlatformWebSearchKey(
    accessToken: string
  ): Promise<PlatformWebSearchKeySummary> {
    return this.deps.admin.getPlatformWebSearchKey(
      (await this.platformActorOf(accessToken, '__platform__'))
    );
  }

  async setPlatformWebSearchKey(
    accessToken: string,
    input: { apiKey: string; idempotencyKey: string }
  ): Promise<PlatformWebSearchKeySummary> {
    return this.deps.admin.setPlatformWebSearchKey(
      (await this.platformActorOf(accessToken, '__platform__')),
      input
    );
  }

  async setTenantWebSearchBackend(
    accessToken: string,
    input: {
      tenantId: string;
      provider: WebSearchNativeProvider;
      status: TenantWebSearchBackend['status'];
      maxUsesDefault?: number;
      idempotencyKey: string;
    }
  ): Promise<TenantWebSearchBackendSummary> {
    return this.deps.admin.setTenantWebSearchBackend(
      (await this.platformActorOf(accessToken, input.tenantId)),
      input
    );
  }

  async setTenantMediaRoute(
    accessToken: string,
    input: {
      tenantId: string;
      kind: TenantMediaRoute['kind'];
      provider: TenantMediaRoute['provider'];
      status: TenantMediaRoute['status'];
      model: string;
      endpointRegion?: string;
      supportsZdr: boolean;
      supportsMam: boolean;
      providerOptions?: Record<string, unknown>;
      maxUsesDefault?: number;
      requiredDataRegion?: string;
      requireZdr: boolean;
      requireMam: boolean;
      externalApis: 'allow' | 'deny';
      idempotencyKey: string;
    }
  ): Promise<TenantMediaRouteSummary> {
    return this.deps.admin.setTenantMediaRoute(
      (await this.platformActorOf(accessToken, input.tenantId)),
      input
    );
  }

  async testProviderKey(
    accessToken: string,
    input: TestProviderKeyInput
  ): Promise<ProviderKeyTestResult> {
    return this.deps.admin.testProviderKey(
      (await this.platformActorOf(accessToken, input.tenantId)),
      input
    );
  }

  async testLiteLLMConnection(
    accessToken: string,
    input: TestLiteLLMConnectionInput
  ): Promise<ProviderKeyTestResult> {
    return this.deps.admin.testLiteLLMConnection(
      (await this.platformActorOf(accessToken, input.tenantId)),
      input
    );
  }

  async getTenantCatalog(
    accessToken: string,
    tenantId: string
  ): Promise<DesktopModelView[]> {
    return this.deps.admin.getTenantCatalog(
      (await this.platformActorOf(accessToken, tenantId)),
      tenantId
    );
  }

  async getTenantAssignment(
    accessToken: string,
    tenantId: string
  ): Promise<TenantAssignment> {
    return this.deps.admin.getTenantModelAssignment(
      (await this.platformActorOf(accessToken, tenantId)),
      tenantId
    );
  }

  async setTenantAssignment(
    accessToken: string,
    input: {
      tenantId: string;
      allowedModelIds: string[];
      defaultMemberModelIds?: string[];
      freeTierModelIds?: string[];
      idempotencyKey: string;
    }
  ): Promise<TenantAssignment> {
    return this.deps.admin.setTenantModelAssignment((await this.platformActorOf(accessToken, input.tenantId)), input);
  }

  async getCurrentRates(accessToken: string): Promise<CreditRateTableView | undefined> {
    return this.deps.admin.getCurrentRates((await this.platformActorOf(accessToken, '__platform__')));
  }

  async getRateSettings(accessToken: string): Promise<CreditRateSettingsView> {
    return this.deps.admin.getRateSettings((await this.platformActorOf(accessToken, '__platform__')));
  }

  async putRateSettings(
    accessToken: string,
    input: PutCreditRateSettingsRequest & { idempotencyKey: string }
  ): Promise<PutCreditRateSettingsResult> {
    const { idempotencyKey, ...body } = input;
    return this.deps.admin.putRateSettings(
      (await this.platformActorOf(accessToken, '__platform__')),
      body,
      { idempotencyKey }
    );
  }

  async adoptPublishedRateSettings(
    accessToken: string,
    idempotencyKey: string
  ): Promise<CreditRateSettingsView> {
    return this.deps.admin.adoptPublishedRateSettings(
      (await this.platformActorOf(accessToken, '__platform__')),
      { idempotencyKey }
    );
  }

  // ── platform: partners ─────────────────────────────────────────────

  async listPartners(accessToken: string): Promise<Partner[]> {
    return this.deps.admin.listPartners((await this.actorOf(accessToken)));
  }
  async createPartner(
    accessToken: string,
    input: {
      name: string;
      slug?: string;
      tenantCap?: number;
      idempotencyKey: string;
    }
  ): Promise<Partner> {
    return this.deps.admin.createPartner((await this.actorOf(accessToken)), input);
  }
  async getPartner(accessToken: string, partnerId: string): Promise<Partner> {
    return this.deps.admin.getPartner((await this.actorOf(accessToken)), partnerId);
  }
  async updatePartner(
    accessToken: string,
    input: {
      partnerId: string;
      tenantCap?: number | null;
      status?: 'active' | 'suspended';
      allowCustomModels?: boolean;
      idempotencyKey: string;
    }
  ): Promise<Partner> {
    return this.deps.admin.updatePartner((await this.actorOf(accessToken)), input);
  }
  async deletePartner(
    accessToken: string,
    input: { partnerId: string; confirmName: string; idempotencyKey: string }
  ): Promise<void> {
    return this.deps.admin.deletePartner((await this.actorOf(accessToken)), input);
  }
  async setTenantCustomModels(
    accessToken: string,
    input: {
      tenantId: string;
      allowCustomModels: boolean;
      idempotencyKey: string;
    }
  ): Promise<Tenant> {
    return this.deps.admin.setTenantCustomModels(
      (await this.actorOf(accessToken)),
      input
    );
  }
  async setTenantSelfSignup(
    accessToken: string,
    input: { tenantId: string; allowed?: boolean; enabled?: boolean; idempotencyKey: string }
  ): Promise<TenantSelfSignupResult> {
    return this.deps.admin.setTenantSelfSignup((await this.actorOf(accessToken)), input);
  }
  async getDefaultSignupTenant(
    accessToken: string
  ): Promise<DefaultSignupTenantSetting> {
    return this.deps.admin.getDefaultSignupTenant((await this.actorOf(accessToken)));
  }
  async setDefaultSignupTenant(
    accessToken: string,
    input: { tenantId: string | null; idempotencyKey: string }
  ): Promise<DefaultSignupTenantSetting> {
    return this.deps.admin.setDefaultSignupTenant((await this.actorOf(accessToken)), input);
  }
  async assignTenantCapabilitySource(
    accessToken: string,
    input: { tenantId: string; sourceId: string; idempotencyKey: string }
  ): Promise<CapabilitySourceAssignment> {
    return this.deps.admin.assignTenantCapabilitySource(
      (await this.actorOf(accessToken)),
      input
    );
  }
  async revokeTenantCapabilitySource(
    accessToken: string,
    input: { tenantId: string; sourceId: string; idempotencyKey: string }
  ): Promise<void> {
    return this.deps.admin.revokeTenantCapabilitySource(
      (await this.actorOf(accessToken)),
      input
    );
  }
  async setTenantCustomerModelLabels(
    accessToken: string,
    input: {
      tenantId: string;
      labels: Record<string, { providerName?: string; modelName?: string }>;
      idempotencyKey: string;
    }
  ): Promise<Tenant> {
    return this.deps.admin.setCustomerModelLabels((await this.actorOf(accessToken)), input);
  }
  async setTenantMcpConnectors(
    accessToken: string,
    input: {
      tenantId: string;
      activeMcpConnectors: string[];
      idempotencyKey: string;
    }
  ): Promise<Tenant> {
    return this.deps.admin.setTenantMcpConnectors(
      (await this.actorOf(accessToken)),
      input
    );
  }
  async getTenantMcpConnectors(
    accessToken: string,
    tenantId: string
  ): Promise<TenantMcpConnectors> {
    return this.deps.admin.getTenantMcpConnectors(
      (await this.actorOf(accessToken)),
      tenantId
    );
  }
  async getOwnMcpConnectors(accessToken: string): Promise<TenantMcpConnectors> {
    return this.deps.admin.getOwnMcpConnectors((await this.actorOf(accessToken)));
  }
  async setTenantPermissionModes(
    accessToken: string,
    input: {
      tenantId: string;
      allowedPermissionModes: AgentPermissionMode[];
      idempotencyKey: string;
    }
  ): Promise<Tenant> {
    return this.deps.admin.setTenantPermissionModes(
      (await this.actorOf(accessToken)),
      input
    );
  }
  async setTenantDeepAgentApprovalTools(
    accessToken: string,
    input: {
      tenantId: string;
      deepAgentApprovalTools: string[];
      idempotencyKey: string;
    }
  ): Promise<Tenant> {
    return this.deps.admin.setTenantDeepAgentApprovalTools(
      (await this.actorOf(accessToken)),
      input
    );
  }
  async getSocialConfig(
    accessToken: string,
    tenantId: string
  ): Promise<TenantSocialConfig> {
    return this.deps.admin.getSocialConfig((await this.actorOf(accessToken)), tenantId);
  }
  async updateSocialMode(
    accessToken: string,
    input: {
      tenantId: string;
      socialLoginMode: SocialLoginMode;
      idempotencyKey: string;
    }
  ): Promise<TenantSocialConfig> {
    const actor = (await this.actorOf(accessToken));
    await this.deps.admin.updateSocialMode(
      actor,
      input.tenantId,
      input.socialLoginMode
    );
    return this.deps.admin.getSocialConfig(actor, input.tenantId);
  }
  async createSocialProvider(
    accessToken: string,
    input: {
      tenantId: string;
      provider: SocialProviderKind;
      displayName: string;
      config?: SocialProviderConfig;
      clientSecret?: string;
      allowJitProvisioning?: boolean;
      allowedEmailDomains?: string[];
      idempotencyKey: string;
    }
  ): Promise<TenantSocialProvider> {
    const { idempotencyKey, ...rest } = input;
    void idempotencyKey;
    return this.deps.admin.createSocialProvider(
      (await this.actorOf(accessToken)),
      rest
    );
  }
  async updateSocialProvider(
    accessToken: string,
    input: {
      tenantId: string;
      providerId: string;
      displayName?: string;
      enabled?: boolean;
      config?: SocialProviderConfig;
      clientSecret?: string;
      allowJitProvisioning?: boolean;
      allowedEmailDomains?: string[];
      idempotencyKey: string;
    }
  ): Promise<TenantSocialProvider> {
    const { tenantId, providerId, idempotencyKey, ...patch } = input;
    void idempotencyKey;
    return this.deps.admin.updateSocialProvider(
      (await this.actorOf(accessToken)),
      tenantId,
      providerId,
      patch
    );
  }
  async deleteSocialProvider(
    accessToken: string,
    input: { tenantId: string; providerId: string; idempotencyKey: string }
  ): Promise<void> {
    return this.deps.admin.deleteSocialProvider(
      (await this.actorOf(accessToken)),
      input.tenantId,
      input.providerId
    );
  }
  async getOrganizationSsoConfig(
    accessToken: string,
    tenantId: string
  ): Promise<TenantOrganizationSsoConfig> {
    return this.deps.admin.getOrganizationSsoConfig(
      (await this.actorOf(accessToken)),
      tenantId
    );
  }
  async updateOrganizationSsoMode(
    accessToken: string,
    input: {
      tenantId: string;
      mode: OrganizationSsoMode;
      confirmBreakGlass?: boolean;
      idempotencyKey: string;
    }
  ): Promise<TenantOrganizationSsoConfig> {
    return this.deps.admin.updateOrganizationSsoMode(
      (await this.actorOf(accessToken)),
      input
    );
  }
  async createOrganizationSsoConnection(
    accessToken: string,
    input: {
      tenantId: string;
      connection: OrganizationSsoConnectionWrite;
      idempotencyKey: string;
    }
  ): Promise<OrganizationSsoConnectionAdmin> {
    return this.deps.admin.createOrganizationSsoConnection(
      (await this.actorOf(accessToken)),
      input
    );
  }
  async updateOrganizationSsoConnection(
    accessToken: string,
    input: {
      tenantId: string;
      connectionId: string;
      patch: OrganizationSsoConnectionPatch;
      idempotencyKey: string;
    }
  ): Promise<OrganizationSsoConnectionAdmin> {
    return this.deps.admin.updateOrganizationSsoConnection(
      (await this.actorOf(accessToken)),
      input
    );
  }
  async deleteOrganizationSsoConnection(
    accessToken: string,
    input: {
      tenantId: string;
      connectionId: string;
      idempotencyKey: string;
    }
  ): Promise<void> {
    return this.deps.admin.deleteOrganizationSsoConnection(
      (await this.actorOf(accessToken)),
      input
    );
  }
  async testOrganizationSsoConnection(
    accessToken: string,
    input: {
      tenantId: string;
      connectionId: string;
      test?: OrganizationSsoConnectionTestInput;
      idempotencyKey: string;
    }
  ): Promise<OrganizationSsoConnectionTestResult> {
    return this.deps.admin.testOrganizationSsoConnection(
      (await this.actorOf(accessToken)),
      input
    );
  }
  async setOrganizationSsoConnectionEnabled(
    accessToken: string,
    input: {
      tenantId: string;
      connectionId: string;
      enabled: boolean;
      idempotencyKey: string;
    }
  ): Promise<OrganizationSsoConnectionAdmin> {
    return this.deps.admin.setOrganizationSsoConnectionEnabled(
      (await this.actorOf(accessToken)),
      input
    );
  }
  async getOrganizationSsoSamlMetadata(
    accessToken: string,
    tenantId: string,
    connectionId: string
  ): Promise<string> {
    return this.deps.admin.getOrganizationSsoSamlMetadata(
      (await this.actorOf(accessToken)),
      tenantId,
      connectionId
    );
  }
  async listPartnerAdmins(
    accessToken: string,
    partnerId: string
  ): Promise<PartnerAdminView[]> {
    return this.deps.admin.listPartnerAdmins(
      (await this.actorOf(accessToken)),
      partnerId
    );
  }
  async listPlatformAdmins(accessToken: string): Promise<PlatformAdminView[]> {
    return this.deps.admin.listPlatformAdmins((await this.actorOf(accessToken)));
  }
  async grantPartnerAdmin(
    accessToken: string,
    input: {
      partnerId: string;
      email: string;
      name: string;
      password: string;
      idempotencyKey: string;
    }
  ): Promise<{ partnerId: string; userId: string }> {
    return this.deps.admin.grantPartnerAdmin((await this.actorOf(accessToken)), input);
  }
  async revokePartnerAdmin(
    accessToken: string,
    input: { partnerId: string; userId: string; idempotencyKey: string }
  ): Promise<void> {
    return this.deps.admin.revokePartnerAdmin((await this.actorOf(accessToken)), input);
  }
  async getPartnerModelScope(
    accessToken: string,
    partnerId: string
  ): Promise<string[]> {
    return this.deps.admin.getPartnerModelScope(
      (await this.actorOf(accessToken)),
      partnerId
    );
  }
  async setPartnerModelScope(
    accessToken: string,
    input: { partnerId: string; modelKeys: string[]; idempotencyKey: string }
  ): Promise<string[]> {
    return this.deps.admin.setPartnerModelScope(
      (await this.actorOf(accessToken)),
      input
    );
  }
  async listPartnerCustomModels(
    accessToken: string,
    partnerId: string
  ): Promise<PartnerCustomModelSummary[]> {
    return this.deps.admin.listPartnerCustomModels(
      (await this.actorOf(accessToken)),
      partnerId
    );
  }
  async registerPartnerCustomModel(
    accessToken: string,
    input: {
      partnerId: string;
      definition: PartnerCustomModelWrite;
      idempotencyKey: string;
    }
  ): Promise<PartnerCustomModelSummary> {
    return this.deps.admin.registerPartnerCustomModel(
      (await this.actorOf(accessToken)),
      input
    );
  }
  async patchPartnerCustomModel(
    accessToken: string,
    input: {
      partnerId: string;
      modelKey: string;
      request: UpdatePartnerCustomModelRequest;
      idempotencyKey: string;
    }
  ): Promise<UpdatePartnerCustomModelResult> {
    return this.deps.admin.patchPartnerCustomModel(
      (await this.actorOf(accessToken)),
      input
    );
  }
  async deletePartnerCustomModel(
    accessToken: string,
    input: { partnerId: string; modelKey: string; idempotencyKey: string }
  ): Promise<void> {
    return this.deps.admin.deletePartnerCustomModel(
      (await this.actorOf(accessToken)),
      input
    );
  }
  async testPartnerCustomModelProvider(
    accessToken: string,
    input: { partnerId: string; request: PartnerCustomModelTestRequest }
  ): Promise<PartnerCustomModelTestResult> {
    return this.deps.admin.testPartnerCustomModelProvider(
      (await this.actorOf(accessToken)),
      input
    );
  }
  async testPartnerCustomModelLlm(
    accessToken: string,
    input: { partnerId: string; request: PartnerCustomModelTestRequest }
  ): Promise<PartnerCustomModelTestResult> {
    return this.deps.admin.testPartnerCustomModelLlm(
      (await this.actorOf(accessToken)),
      input
    );
  }
  async getPartnerMcpConnectorScope(
    accessToken: string,
    partnerId: string
  ): Promise<string[]> {
    return this.deps.admin.getPartnerMcpConnectorScope(
      (await this.actorOf(accessToken)),
      partnerId
    );
  }
  async setPartnerMcpConnectorScope(
    accessToken: string,
    input: {
      partnerId: string;
      connectorIds: string[];
      idempotencyKey: string;
    }
  ): Promise<string[]> {
    return this.deps.admin.setPartnerMcpConnectorScope(
      (await this.actorOf(accessToken)),
      input
    );
  }

  // ── capability sources (skill-agent-distribution PR10) ──────────────

  async listCapabilitySources(
    accessToken: string
  ): Promise<CapabilitySourceRecord[]> {
    return this.deps.admin.listCapabilitySources((await this.actorOf(accessToken)), {});
  }
  async registerCapabilitySource(
    accessToken: string,
    input: CapabilitySourceRegisterInput & { idempotencyKey: string }
  ): Promise<CapabilitySourceRecord> {
    return this.deps.admin.registerCapabilitySource(
      (await this.actorOf(accessToken)),
      input
    );
  }
  async deleteCapabilitySource(
    accessToken: string,
    id: string,
    idempotencyKey: string
  ): Promise<{ success: boolean }> {
    await this.deps.admin.deleteCapabilitySource((await this.actorOf(accessToken)), {
      id,
      idempotencyKey,
    });
    return { success: true };
  }
  async setPartnerCapabilitySourceScope(
    accessToken: string,
    input: { partnerId: string; sourceIds: string[]; idempotencyKey: string }
  ): Promise<string[]> {
    return this.deps.admin.setPartnerCapabilitySourceScope(
      (await this.actorOf(accessToken)),
      input
    );
  }

  // ── partner console (scope==='partner') ────────────────────────────

  partnerLoginWithPassword(input: {
    sessionProtocol?: SessionProtocol;
    email: string;
    password: string;
  }): Promise<TokenPair & { partnerId: string }> {
    return this.deps.api.loginPartnerWithPassword(input.email, input.password, input.sessionProtocol);
  }
  async partnerSelf(accessToken: string): Promise<Partner> {
    const actor = (await this.actorOf(accessToken));
    return this.deps.admin.getPartner(actor, actor.partnerId ?? '');
  }
  async partnerOverview(accessToken: string, query?: Pick<UsageQuery, 'from' | 'to'>): Promise<PartnerOverview> {
    return this.deps.admin.partnerOverview((await this.actorOf(accessToken)), query);
  }
  async partnerListAdmins(accessToken: string): Promise<PartnerAdminView[]> {
    const actor = (await this.actorOf(accessToken));
    return this.deps.admin.listPartnerAdmins(actor, actor.partnerId ?? '');
  }
  async partnerGrantAdmin(accessToken: string, input: { email: string; name: string; password?: string; idempotencyKey: string }): Promise<{ partnerId: string; userId: string }> {
    const actor = (await this.actorOf(accessToken));
    return this.deps.admin.grantPartnerAdmin(actor, { ...input, partnerId: actor.partnerId ?? '', password: input.password ?? '' });
  }
  async partnerRevokeAdmin(accessToken: string, input: { userId: string; idempotencyKey: string }): Promise<void> {
    const actor = (await this.actorOf(accessToken));
    return this.deps.admin.revokePartnerAdmin(actor, { ...input, partnerId: actor.partnerId ?? '' });
  }
  async partnerSelfModelScope(accessToken: string): Promise<string[]> {
    const actor = (await this.actorOf(accessToken));
    return this.deps.admin.getPartnerModelScope(actor, actor.partnerId ?? '');
  }
  async partnerListCustomModels(
    accessToken: string
  ): Promise<PartnerCustomModelSummary[]> {
    const actor = (await this.actorOf(accessToken));
    return this.deps.admin.listPartnerCustomModels(
      actor,
      actor.partnerId ?? ''
    );
  }
  async partnerListTenants(accessToken: string): Promise<Tenant[]> {
    return this.deps.admin.listPartnerTenants((await this.actorOf(accessToken)));
  }
  async partnerCreateTenant(
    accessToken: string,
    input: {
      name: string;
      slug?: string;
      deploymentProfile?: DeploymentProfile;
      idempotencyKey: string;
    }
  ): Promise<Tenant> {
    return this.deps.admin.createTenant((await this.actorOf(accessToken)), input);
  }
  async partnerActAsTenant(
    accessToken: string,
    tenantId: string
  ): Promise<ActAsSessionResult> {
    return this.deps.api.issuePartnerActAsSession(
      (await this.actorOf(accessToken)),
      tenantId, accessToken
    );
  }
  async partnerGetTenantAssignment(
    accessToken: string,
    tenantId: string
  ): Promise<TenantAssignment> {
    return this.deps.admin.getTenantModelAssignment(
      (await this.actorOf(accessToken)),
      tenantId
    );
  }
  async partnerSetTenantAssignment(
    accessToken: string,
    input: {
      tenantId: string;
      allowedModelIds: string[];
      freeTierModelIds?: string[];
      idempotencyKey: string;
    }
  ): Promise<TenantAssignment> {
    return this.deps.admin.setTenantModelAssignment(
      (await this.actorOf(accessToken)),
      input
    );
  }
  async platformDeployCustomModel(
    accessToken: string,
    input: { tenantId: string; customModelKey: string; idempotencyKey: string }
  ): Promise<PartnerCustomModelDeployment> {
    const item = await this.deps.admin.deployPartnerCustomModel(
      (await this.actorOf(accessToken)),
      input
    );
    return { catalogModelId: item.id };
  }
  async platformRevokeCustomModel(
    accessToken: string,
    input: { tenantId: string; customModelKey: string; idempotencyKey: string }
  ): Promise<void> {
    await this.deps.admin.revokePartnerCustomModel(
      (await this.actorOf(accessToken)),
      input
    );
  }
  async partnerGetTenantMcpConnectors(
    accessToken: string,
    tenantId: string
  ): Promise<TenantMcpConnectors> {
    return this.deps.admin.getTenantMcpConnectors(
      (await this.actorOf(accessToken)),
      tenantId
    );
  }
  async partnerSetTenantMcpConnectors(
    accessToken: string,
    input: {
      tenantId: string;
      activeMcpConnectors: string[];
      idempotencyKey: string;
    }
  ): Promise<Tenant> {
    return this.deps.admin.setTenantMcpConnectors(
      (await this.actorOf(accessToken)),
      input
    );
  }
  async partnerSetTenantSelfSignup(
    accessToken: string,
    input: { tenantId: string; enabled: boolean; idempotencyKey: string }
  ): Promise<TenantSelfSignupResult> {
    return this.deps.admin.setTenantSelfSignup((await this.actorOf(accessToken)), input);
  }
  async partnerSetTenantPermissionModes(
    accessToken: string,
    input: {
      tenantId: string;
      allowedPermissionModes: AgentPermissionMode[];
      idempotencyKey: string;
    }
  ): Promise<Tenant> {
    return this.deps.admin.setTenantPermissionModes(
      (await this.actorOf(accessToken)),
      input
    );
  }
  async partnerSetTenantDeepAgentApprovalTools(
    accessToken: string,
    input: {
      tenantId: string;
      deepAgentApprovalTools: string[];
      idempotencyKey: string;
    }
  ): Promise<Tenant> {
    return this.deps.admin.setTenantDeepAgentApprovalTools(
      (await this.actorOf(accessToken)),
      input
    );
  }

  // ── credit allocation (Phase 2) ────────────────────────────────────

  async setTenantAllocation(
    accessToken: string,
    input: AllocationInput & { tenantId: string }
  ): Promise<CreditAllocation> {
    const { tenantId, ...rest } = input;
    return this.deps.admin.allocateCredit((await this.actorOf(accessToken)), {
      ...rest,
      granteeKind: 'tenant',
      granteeId: tenantId,
    });
  }
  async getTenantAllocation(
    accessToken: string,
    tenantId: string
  ): Promise<AllocationOverview> {
    return this.deps.admin.getAllocationOverview(
      (await this.actorOf(accessToken)),
      'tenant',
      tenantId
    );
  }
  async setPartnerAllocation(
    accessToken: string,
    input: AllocationInput & { partnerId: string }
  ): Promise<CreditAllocation> {
    const { partnerId, ...rest } = input;
    return this.deps.admin.allocateCredit((await this.actorOf(accessToken)), {
      ...rest,
      granteeKind: 'partner',
      granteeId: partnerId,
    });
  }
  async getPartnerAllocation(
    accessToken: string,
    partnerId: string
  ): Promise<AllocationOverview> {
    return this.deps.admin.getAllocationOverview(
      (await this.actorOf(accessToken)),
      'partner',
      partnerId
    );
  }
  async getPartnerWebSearchUsage(
    accessToken: string,
    partnerId: string,
    query?: Pick<UsageQuery, 'from' | 'to'>
  ): Promise<PartnerWebSearchUsageSummary> {
    return this.deps.admin.partnerWebSearchUsage(
      (await this.actorOf(accessToken)),
      partnerId,
      query
    );
  }
  async partnerGetOwnAllocation(accessToken: string): Promise<AllocationOverview> {
    const actor = (await this.actorOf(accessToken));
    return this.deps.admin.getAllocationOverview(
      actor,
      'partner',
      actor.partnerId ?? ''
    );
  }
  async partnerGetOwnWebSearchUsage(
    accessToken: string,
    query?: Pick<UsageQuery, 'from' | 'to'>
  ): Promise<PartnerWebSearchUsageSummary> {
    const actor = (await this.actorOf(accessToken));
    return this.deps.admin.partnerWebSearchUsage(
      actor,
      actor.partnerId ?? '',
      query
    );
  }
  async partnerSetTenantAllocation(
    accessToken: string,
    input: AllocationInput & { tenantId: string }
  ): Promise<CreditAllocation> {
    const { tenantId, ...rest } = input;
    return this.deps.admin.allocateCredit((await this.actorOf(accessToken)), {
      ...rest,
      granteeKind: 'tenant',
      granteeId: tenantId,
    });
  }
  async partnerGetTenantAllocation(
    accessToken: string,
    tenantId: string
  ): Promise<AllocationOverview> {
    return this.deps.admin.getAllocationOverview(
      (await this.actorOf(accessToken)),
      'tenant',
      tenantId
    );
  }

  async tenantGetOwnAllocation(accessToken: string): Promise<AllocationOverview> {
    const actor = (await this.actorOf(accessToken));
    return this.deps.admin.getAllocationOverview(
      actor,
      'tenant',
      actor.tenantId
    );
  }

  async setSubjectAllocation(
    accessToken: string,
    input: AllocationInput & {
      granteeKind: 'group' | 'user';
      granteeId: string;
    }
  ): Promise<CreditAllocation> {
    return this.deps.admin.allocateCredit((await this.actorOf(accessToken)), input);
  }
  async setSubjectAllocationsBatch(
    accessToken: string,
    input: AllocationBatchInput
  ): Promise<AllocationBatchResult> {
    return this.deps.admin.allocateCreditBatch(
      (await this.actorOf(accessToken)),
      input
    );
  }
  async setMemberQuotaDefault(
    accessToken: string,
    input: MemberQuotaDefaultInput
  ): Promise<MemberQuotaDefault> {
    return this.deps.admin.setMemberQuotaDefault(
      (await this.actorOf(accessToken)),
      input
    );
  }
  async getMemberQuotaDefault(
    accessToken: string,
    nodeKind: MemberQuotaDefault['nodeKind'],
    nodeId: string
  ): Promise<MemberQuotaDefault | null> {
    return this.deps.admin
      .getMemberQuotaDefault((await this.actorOf(accessToken)), nodeKind, nodeId)
      .then((row) => row ?? null);
  }

  async listCreditLimits(
    accessToken: string
  ): Promise<{ items: CreditLimitView[]; advancedPolicyCount: number }> {
    return this.deps.admin.listCreditLimits(
      (await this.actorOf(accessToken)),
      this.quotaPolicies
    );
  }
  async upsertCreditLimit(
    accessToken: string,
    input: UpsertCreditLimitRequest & { idempotencyKey: string }
  ): Promise<CreditLimitView> {
    return this.deps.admin.upsertCreditLimit((await this.actorOf(accessToken)), {
      ...input,
      quotaPolicies: this.quotaPolicies,
    });
  }
  async previewEffectiveCreditLimits(
    accessToken: string,
    input: EffectiveCreditLimitsRequest
  ): Promise<EffectiveCreditLimitsView> {
    return this.deps.admin.previewEffectiveCreditLimits(
      (await this.actorOf(accessToken)),
      { ...input, quotaPolicies: this.quotaPolicies }
    );
  }
  async deleteCreditLimit(
    accessToken: string,
    input: { id: string; idempotencyKey: string }
  ): Promise<{ ok: true; deleted: boolean }> {
    return this.deps.admin.deleteCreditLimit((await this.actorOf(accessToken)), {
      ...input,
      quotaPolicies: this.quotaPolicies,
    });
  }

  // ── announcements ───────────────────────────────────────────────────────
  // The AdminService method takes the surface as an argument, so each of these
  // is the same call with a different scope literal.

  async listAnnouncements(
    accessToken: string,
    params?: AnnouncementListQuery
  ): Promise<Paginated<Announcement>> {
    return this.deps.admin.listAnnouncements(
      (await this.actorOf(accessToken)),
      'tenant',
      params ?? {}
    );
  }
  async createAnnouncement(
    accessToken: string,
    input: CreateAnnouncementRequest & { idempotencyKey: string }
  ): Promise<Announcement> {
    return this.deps.admin.createAnnouncement(
      (await this.actorOf(accessToken)),
      'tenant',
      input
    );
  }
  async patchAnnouncement(
    accessToken: string,
    input: PatchAnnouncementRequest & { id: string; idempotencyKey: string }
  ): Promise<Announcement> {
    return this.deps.admin.patchAnnouncement(
      (await this.actorOf(accessToken)),
      'tenant',
      input
    );
  }
  async publishAnnouncement(
    accessToken: string,
    input: { id: string; idempotencyKey: string }
  ): Promise<Announcement> {
    return this.deps.admin.publishAnnouncement(
      (await this.actorOf(accessToken)),
      'tenant',
      input
    );
  }
  async archiveAnnouncement(
    accessToken: string,
    input: { id: string; idempotencyKey: string }
  ): Promise<Announcement> {
    return this.deps.admin.archiveAnnouncement(
      (await this.actorOf(accessToken)),
      'tenant',
      input
    );
  }

  async listPartnerAnnouncements(
    accessToken: string,
    params?: AnnouncementListQuery
  ): Promise<Paginated<Announcement>> {
    return this.deps.admin.listAnnouncements(
      (await this.actorOf(accessToken)),
      'partner',
      params ?? {}
    );
  }
  async createPartnerAnnouncement(
    accessToken: string,
    input: CreateAnnouncementRequest & { idempotencyKey: string }
  ): Promise<Announcement> {
    return this.deps.admin.createAnnouncement(
      (await this.actorOf(accessToken)),
      'partner',
      input
    );
  }
  async patchPartnerAnnouncement(
    accessToken: string,
    input: PatchAnnouncementRequest & { id: string; idempotencyKey: string }
  ): Promise<Announcement> {
    return this.deps.admin.patchAnnouncement(
      (await this.actorOf(accessToken)),
      'partner',
      input
    );
  }
  async publishPartnerAnnouncement(
    accessToken: string,
    input: { id: string; idempotencyKey: string }
  ): Promise<Announcement> {
    return this.deps.admin.publishAnnouncement(
      (await this.actorOf(accessToken)),
      'partner',
      input
    );
  }
  async archivePartnerAnnouncement(
    accessToken: string,
    input: { id: string; idempotencyKey: string }
  ): Promise<Announcement> {
    return this.deps.admin.archiveAnnouncement(
      (await this.actorOf(accessToken)),
      'partner',
      input
    );
  }

  async listPlatformAnnouncements(
    accessToken: string,
    params?: AnnouncementListQuery
  ): Promise<Paginated<Announcement>> {
    return this.deps.admin.listAnnouncements(
      (await this.actorOf(accessToken)),
      'platform',
      params ?? {}
    );
  }
  async createPlatformAnnouncement(
    accessToken: string,
    input: CreateAnnouncementRequest & { idempotencyKey: string }
  ): Promise<Announcement> {
    return this.deps.admin.createAnnouncement(
      (await this.actorOf(accessToken)),
      'platform',
      input
    );
  }
  async patchPlatformAnnouncement(
    accessToken: string,
    input: PatchAnnouncementRequest & { id: string; idempotencyKey: string }
  ): Promise<Announcement> {
    return this.deps.admin.patchAnnouncement(
      (await this.actorOf(accessToken)),
      'platform',
      input
    );
  }
  async publishPlatformAnnouncement(
    accessToken: string,
    input: { id: string; idempotencyKey: string }
  ): Promise<Announcement> {
    return this.deps.admin.publishAnnouncement(
      (await this.actorOf(accessToken)),
      'platform',
      input
    );
  }
  async archivePlatformAnnouncement(
    accessToken: string,
    input: { id: string; idempotencyKey: string }
  ): Promise<Announcement> {
    return this.deps.admin.archiveAnnouncement(
      (await this.actorOf(accessToken)),
      'platform',
      input
    );
  }
  async platformDiagnosticBundles(
    accessToken: string,
    query: DiagnosticBundleListQuery = {}
  ): Promise<Paginated<DiagnosticBundleView>> {
    return this.deps.admin.listDiagnosticBundles((await this.actorOf(accessToken)), query);
  }
  async platformDiagnosticBundleDownload(
    accessToken: string,
    id: string
  ): Promise<{ url: string; expiresAt: string }> {
    const actor = (await this.actorOf(accessToken));
    const bundle = await this.deps.admin.getDiagnosticBundleForDownload(actor, id);
    if (!this.deps.diagnosticsIntake) {
      throw new ServiceError('diagnostics_unavailable', 503);
    }
    const url = await this.deps.diagnosticsIntake.createSignedUrl(
      bundle.objectPath,
      DIAGNOSTICS_SIGNED_URL_TTL_SECONDS
    );
    const expiresAt = new Date(
      Date.now() + DIAGNOSTICS_SIGNED_URL_TTL_SECONDS * 1000
    ).toISOString();
    await this.deps.admin.recordDiagnosticBundleDownload(actor, bundle, expiresAt);
    return { url, expiresAt };
  }

  async listMyAnnouncements(
    accessToken: string,
    params?: { limit?: number; offset?: number }
  ): Promise<MeAnnouncementsResponse> {
    const { tenantId, userId } = (await this.verify(accessToken));
    return this.deps.api.listMyAnnouncements(tenantId, userId, params ?? {});
  }
  async markAnnouncementRead(accessToken: string, id: string): Promise<{ ok: true }> {
    const { tenantId, userId } = (await this.verify(accessToken));
    return this.deps.api.markMyAnnouncementRead(tenantId, userId, id);
  }
  async markAllAnnouncementsRead(
    accessToken: string
  ): Promise<MarkAllAnnouncementsReadResult> {
    const { tenantId, userId } = (await this.verify(accessToken));
    return this.deps.api.markAllMyAnnouncementsRead(tenantId, userId);
  }

  async listQuotaProfiles(accessToken: string): Promise<QuotaProfileListView> {
    return this.deps.admin.listQuotaProfiles((await this.actorOf(accessToken)));
  }
  async createQuotaProfile(
    accessToken: string,
    input: CreateQuotaProfileRequest & { idempotencyKey: string }
  ): Promise<QuotaProfileView> {
    return this.deps.admin.createQuotaProfile((await this.actorOf(accessToken)), input);
  }
  async updateQuotaProfile(
    accessToken: string,
    input: UpdateQuotaProfileRequest & { id: string; idempotencyKey: string }
  ): Promise<QuotaProfileView> {
    return this.deps.admin.updateQuotaProfile((await this.actorOf(accessToken)), input);
  }
  async deactivateQuotaProfile(
    accessToken: string,
    input: { id: string; idempotencyKey: string }
  ): Promise<QuotaProfileView> {
    return this.deps.admin.deactivateQuotaProfile((await this.actorOf(accessToken)), input);
  }
  async previewQuotaProfileImpact(
    accessToken: string,
    input: {
      id: string;
      limits?: QuotaProfileLimitInput[];
      status?: 'active' | 'inactive';
    }
  ): Promise<QuotaProfileAssignmentPreview> {
    return this.deps.admin.previewQuotaProfileImpact(
      (await this.actorOf(accessToken)),
      input
    );
  }
  async listUserQuotaAssignments(
    accessToken: string,
    query: UserQuotaAssignmentQuery = {}
  ): Promise<Paginated<UserQuotaAssignmentView>> {
    return this.deps.admin.listUserQuotaAssignments(
      (await this.actorOf(accessToken)),
      query
    );
  }
  async assignQuotaProfile(
    accessToken: string,
    input: { userId: string; quotaProfileId: string; idempotencyKey: string }
  ): Promise<MembershipQuotaAssignment> {
    return this.deps.admin.assignQuotaProfileManually(
      (await this.actorOf(accessToken)),
      input
    );
  }
  async clearQuotaProfilePin(
    accessToken: string,
    input: { userId: string; idempotencyKey: string }
  ): Promise<MembershipQuotaAssignment | null> {
    return this.deps.admin
      .clearQuotaProfilePin((await this.actorOf(accessToken)), input)
      .then((row) => row ?? null);
  }

  async getSubjectAllocation(
    accessToken: string,
    granteeKind: 'group' | 'user',
    granteeId: string
  ): Promise<AllocationOverview> {
    return this.deps.admin.getAllocationOverview(
      (await this.actorOf(accessToken)),
      granteeKind,
      granteeId
    );
  }

  // ── admin: read ────────────────────────────────────────────────────

  async overview(accessToken: string): Promise<AdminOverview> {
    const actor = (await this.actorOf(accessToken));
    const admin = this.deps.admin;
    return {
      users: await admin.countUsers(actor),
      groups: (await admin.listGroups(actor)).length,
      catalogModels: await admin.countCatalogModels(actor),
      gatewayBackends: (await this.deps.api.listGatewayBackends(actor.tenantId))
        .length,
    };
  }
  listUsers(accessToken: string): Promise<AdminUserView[]>;
  listUsers(
    accessToken: string,
    query: UserListQuery
  ): Promise<Paginated<AdminUserView>>;
  async listUsers(
    accessToken: string,
    query?: UserListQuery
  ): Promise<AdminUserView[] | Paginated<AdminUserView>> {
    const actor = (await this.actorOf(accessToken));
    return query
      ? this.deps.admin.queryUsers(actor, query)
      : this.deps.admin.listUsers(actor);
  }
  async listGroups(accessToken: string): Promise<AdminGroupView[]> {
    const actor = (await this.actorOf(accessToken));
    const groups = await this.deps.admin.listGroups(actor);
    const memberships = await Promise.all(
      groups.map((group) => this.deps.admin.listGroupMemberIds(actor, group.id))
    );
    return groups.map((group: Group, index) => ({
      ...group,
      memberIds: memberships[index]!,
    }));
  }
  async listModelAccess(accessToken: string): Promise<TenantModelAccess[]> {
    return this.deps.admin.listModelAccess((await this.actorOf(accessToken)));
  }
  async getTenantLoginDefaultModel(accessToken: string): Promise<TenantLoginDefaultModelView> {
    return this.deps.admin.getTenantLoginDefaultModel((await this.actorOf(accessToken)));
  }
  async previewModels(accessToken: string): Promise<DesktopModelView[]> {
    const { tenantId, userId } = (await this.verify(accessToken));
    return this.deps.api.availableModels(tenantId, userId);
  }
  async listModelRoutes(accessToken: string): Promise<ModelRouteAdminView[]> {
    const actor = (await this.actorOf(accessToken));
    return this.deps.api.listModelRoutes(actor.tenantId);
  }
  async listGatewayBackends(accessToken: string): Promise<GatewayBackendAdminView[]> {
    const actor = (await this.actorOf(accessToken));
    return this.deps.api.listGatewayBackends(actor.tenantId);
  }
  async previewRoutes(
    accessToken: string
  ): Promise<{ candidates: ModelRouteAdminView[] }> {
    const actor = (await this.actorOf(accessToken));
    return { candidates: await this.deps.api.listModelRoutes(actor.tenantId) };
  }
  async listRoutesHealth(
    accessToken: string
  ): Promise<{ backends: Array<{ id: string; status: string }> }> {
    const actor = (await this.actorOf(accessToken));
    const backends = await this.deps.api.listGatewayBackends(actor.tenantId);
    return {
      backends: backends.map((b) => ({ id: b.id, status: b.status })),
    };
  }
  async previewPolicy(
    _accessToken: string,
    draft: MergePolicyInput
  ): Promise<PolicyContextSnapshot> {
    return this.deps.admin.previewPolicy((await this.actorOf(_accessToken)), draft);
  }
  listAudit(accessToken: string): Promise<AdminAuditEvent[]>;
  listAudit(
    accessToken: string,
    query: AuditListQuery
  ): Promise<Paginated<AdminAuditEvent>>;
  async listAudit(
    accessToken: string,
    query?: AuditListQuery
  ): Promise<AdminAuditEvent[] | Paginated<AdminAuditEvent>> {
    const actor = (await this.actorOf(accessToken));
    return query
      ? this.deps.admin.queryAudit(actor, query)
      : this.deps.admin.listAudit(actor);
  }
  async exportAudit(accessToken: string): Promise<AdminAuditEvent[]> {
    return this.deps.admin.exportAudit((await this.actorOf(accessToken)));
  }
  async listQuotas(accessToken: string): Promise<QuotaPolicy[]> {
    return this.deps.admin.listQuotas(
      (await this.actorOf(accessToken)),
      this.quotaPolicies
    );
  }
  async usageSummary(
    accessToken: string,
    query?: UsageQuery
  ): Promise<UsageSummaryView> {
    return this.deps.admin.usageSummary((await this.actorOf(accessToken)), query);
  }
  async usageSeries(
    accessToken: string,
    query?: UsageSeriesQuery
  ): Promise<UsageSeriesPoint[]> {
    return this.deps.admin.usageSeries((await this.actorOf(accessToken)), query);
  }
  async usageEvents(
    accessToken: string,
    query?: UsageQuery
  ): Promise<Paginated<UsageEventView>> {
    return this.deps.admin.usageEvents((await this.actorOf(accessToken)), query);
  }
  async usageVerify(
    accessToken: string,
    query?: UsageQuery
  ): Promise<UsageChainVerification> {
    return this.deps.admin.usageVerify((await this.actorOf(accessToken)), query);
  }
  async usageExport(accessToken: string, query?: UsageQuery): Promise<string> {
    return this.deps.admin.usageExportCsv((await this.actorOf(accessToken)), query);
  }
  async creditSettlement(
    accessToken: string,
    query?: CreditSettlementQuery
  ): Promise<CreditSettlementReport> {
    return this.deps.admin.creditSettlement(
      (await this.actorOf(accessToken)),
      query,
      this.quotaPolicies
    );
  }
  async creditSettlementExport(
    accessToken: string,
    query?: CreditSettlementQuery
  ): Promise<string> {
    return this.deps.admin.creditSettlementCsv(
      (await this.actorOf(accessToken)),
      query,
      this.quotaPolicies
    );
  }

  // ── admin: write ───────────────────────────────────────────────────

  async inviteUser(
    accessToken: string,
    input: {
      email: string;
      name: string;
      roles?: ServiceRole[];
      idempotencyKey: string;
    }
  ): Promise<InvitedUser> {
    const actor = (await this.actorOf(accessToken));
    return this.deps.admin.inviteUser(actor, {
      idempotencyKey: input.idempotencyKey,
      email: input.email,
      name: input.name,
      ...(input.roles === undefined ? {} : { roles: input.roles }),
    });
  }
  async createUserWithPassword(
    accessToken: string,
    input: {
      email: string;
      name: string;
      password: string;
      roles?: ServiceRole[];
      idempotencyKey: string;
    }
  ): Promise<CreatedUserResult> {
    const actor = (await this.actorOf(accessToken));
    return this.deps.admin.createUserWithPassword(actor, {
      idempotencyKey: input.idempotencyKey,
      email: input.email,
      name: input.name,
      password: input.password,
      ...(input.roles === undefined ? {} : { roles: input.roles }),
    });
  }
  async updateUserRoles(
    accessToken: string,
    input: { userId: string; roles: ServiceRole[]; idempotencyKey: string }
  ): Promise<void> {
    return this.deps.admin.updateUserRoles((await this.actorOf(accessToken)), input);
  }
  async disableUser(
    accessToken: string,
    input: { userId: string; idempotencyKey: string }
  ): Promise<void> {
    return this.deps.admin.disableUser((await this.actorOf(accessToken)), input);
  }
  async enableUser(
    accessToken: string,
    input: { userId: string; idempotencyKey: string }
  ): Promise<void> {
    return this.deps.admin.enableUser((await this.actorOf(accessToken)), input);
  }
  async deleteUser(
    accessToken: string,
    input: { userId: string; idempotencyKey: string }
  ): Promise<void> {
    return this.deps.admin.deleteUser((await this.actorOf(accessToken)), input);
  }
  async grantPlatformAdmin(
    accessToken: string,
    input: {
      userId?: string;
      email?: string;
      name?: string;
      password?: string;
      idempotencyKey: string;
    }
  ): Promise<{ userId: string }> {
    return this.deps.admin.grantPlatformAdmin((await this.actorOf(accessToken)), input);
  }
  async revokePlatformAdmin(
    accessToken: string,
    input: { userId: string; idempotencyKey: string }
  ): Promise<void> {
    return this.deps.admin.revokePlatformAdmin(
      (await this.actorOf(accessToken)),
      input
    );
  }
  async assignSeat(
    accessToken: string,
    input: {
      userId: string;
      seatStatus: 'assigned' | 'unassigned' | 'suspended';
      idempotencyKey: string;
    }
  ): Promise<void> {
    return this.deps.admin.assignSeat((await this.actorOf(accessToken)), input);
  }
  async updateDownloadAccess(
    accessToken: string,
    input: {
      userId: string;
      desktopAccess?: boolean;
      downloadChannels?: DownloadChannel[];
      allowedPlatforms?: DesktopPlatform[];
      minimumDesktopVersion?: string;
      idempotencyKey: string;
    }
  ): Promise<void> {
    return this.deps.admin
      .updateDownloadAccess((await this.actorOf(accessToken)), input)
      .then(() => undefined);
  }
  async createGroup(
    accessToken: string,
    input: { name: string; idempotencyKey: string }
  ): Promise<Group> {
    return this.deps.admin.createGroup((await this.actorOf(accessToken)), input);
  }
  async updateGroupMembers(
    accessToken: string,
    input: {
      groupId: string;
      addUserId?: string;
      removeUserId?: string;
      idempotencyKey: string;
    }
  ): Promise<Group> {
    return this.deps.admin.updateGroupMembers((await this.actorOf(accessToken)), input);
  }
  async updateAllowlist(
    accessToken: string,
    input: ModelAccessSubject & { idempotencyKey: string }
  ): Promise<TenantModelAccess> {
    return this.deps.admin.updateAllowlist((await this.actorOf(accessToken)), input);
  }
  async updateTenantLoginDefaultModel(
    accessToken: string,
    input: { loginDefaultModelId: string | null; idempotencyKey: string }
  ): Promise<TenantLoginDefaultModelView> {
    return this.deps.admin.updateTenantLoginDefaultModel((await this.actorOf(accessToken)), input);
  }
  async setCustomerModelLabels(
    accessToken: string,
    input: { labels: Record<string, { providerName?: string; modelName?: string }>; idempotencyKey: string }
  ): Promise<Tenant> {
    return this.deps.admin.setCustomerModelLabels((await this.actorOf(accessToken)), input);
  }
  async createModelRoute(
    accessToken: string,
    input: {
      route: ModelRoute;
      catalogItem: VelugaModelCatalogItem;
      idempotencyKey: string;
    }
  ): Promise<ModelRouteAdminView> {
    await this.deps.admin.registerCustomProviderModel(
      (await this.actorOf(accessToken)),
      input
    );
    const registered = (
      await this.deps.api.listModelRoutes(input.route.tenantId)
    ).find((route) => route.id === input.route.id);
    if (!registered) throw new Error('model_route_not_found');
    return registered;
  }
  async removeCustomProviderModel(
    accessToken: string,
    input: { catalogModelId: string; idempotencyKey: string }
  ): Promise<void> {
    return this.deps.admin.removeCustomProviderModel(
      (await this.actorOf(accessToken)),
      input
    );
  }
  async createGatewayBackend(
    accessToken: string,
    input: { backend: GatewayBackend; idempotencyKey: string }
  ): Promise<void> {
    return this.deps.admin.registerGatewayBackend(
      (await this.actorOf(accessToken)),
      input
    );
  }
  async patchGatewayBackend(
    accessToken: string,
    input: {
      backendId: string;
      backend: Partial<GatewayBackend>;
      idempotencyKey: string;
    }
  ): Promise<void> {
    // The current `registerGatewayBackend` upserts by id; we route the patch
    // through the same code path so the in-process client behaves identically
    // to the HTTP `PATCH /v1/admin/gateway-backends/:id` until the dedicated
    // `updateGatewayBackend` lands in AdminService.
    return this.deps.admin.registerGatewayBackend((await this.actorOf(accessToken)), {
      idempotencyKey: input.idempotencyKey,
      backend: { ...(input.backend as GatewayBackend), id: input.backendId },
    });
  }
  async updateRoutingPolicy(
    accessToken: string,
    input: {
      modelId: string;
      routing: ModelRoutingPolicy;
      idempotencyKey: string;
    }
  ): Promise<void> {
    return this.deps.admin.updateRoutingPolicy(
      (await this.actorOf(accessToken)),
      input
    );
  }
  async setQuota(
    accessToken: string,
    input: { quota: QuotaPolicy; idempotencyKey: string }
  ): Promise<void> {
    return this.deps.admin.setQuota((await this.actorOf(accessToken)), {
      ...input,
      quotaPolicies: this.quotaPolicies,
    });
  }
  async updateDownloadChannels(
    accessToken: string,
    input: {
      userId: string;
      desktopAccess?: boolean;
      downloadChannels?: DownloadChannel[];
      allowedPlatforms?: DesktopPlatform[];
      minimumDesktopVersion?: string;
      idempotencyKey: string;
    }
  ): Promise<unknown> {
    return this.deps.admin.updateDownloadAccess(
      (await this.actorOf(accessToken)),
      input
    );
  }
  async downloadLatestForUser(
    accessToken: string,
    input: {
      userId: string;
      channel: DownloadChannel;
      platform: DesktopPlatform;
    }
  ): Promise<DownloadManifest> {
    const actor = (await this.actorOf(accessToken));
    return this.deps.api.downloadLatest(
      actor.tenantId,
      input.userId,
      input.channel,
      input.platform
    );
  }
  async listExtensionReleases(accessToken: string, extensionId: string, platform: 'win32' | 'darwin'): Promise<ExtensionReleaseView[]> {
    return this.deps.admin.listExtensionReleases((await this.actorOf(accessToken)), extensionId, platform);
  }
  async promoteExtensionRelease(
    accessToken: string,
    input: { extensionId: string; platform: 'win32' | 'darwin'; version: string; idempotencyKey: string }
  ): Promise<void> {
    return this.deps.admin.setStableExtensionRelease((await this.actorOf(accessToken)), { ...input, mode: 'promote' });
  }
  async rollbackExtensionRelease(
    accessToken: string,
    input: { extensionId: string; platform: 'win32' | 'darwin'; version: string; idempotencyKey: string }
  ): Promise<void> {
    return this.deps.admin.setStableExtensionRelease((await this.actorOf(accessToken)), { ...input, mode: 'rollback' });
  }
  async withdrawExtensionRelease(
    accessToken: string,
    input: { extensionId: string; platform: 'win32' | 'darwin'; idempotencyKey: string }
  ): Promise<void> {
    return this.deps.admin.withdrawStableExtensionRelease((await this.actorOf(accessToken)), input);
  }

  // ── internals ──────────────────────────────────────────────────────

  private async verify(accessToken: string): Promise<ServiceTokenClaims> {
    return this.deps.api.verifyActiveToken(accessToken, 'access');
  }
  private async actorOf(accessToken: string): Promise<Actor> {
    const claims = (await this.verify(accessToken));
    return {
      tenantId: claims.tenantId,
      userId: claims.userId,
      ...(claims.scope === undefined ? {} : { scope: claims.scope }),
      ...(claims.partnerId === undefined ? {} : { partnerId: claims.partnerId }),
      ...(claims.actAs === undefined ? {} : { actAs: claims.actAs }),
    };
  }
  private async platformActorOf(accessToken: string, tenantId: string): Promise<Actor> {
    const claims = (await this.verify(accessToken));
    return {
      tenantId,
      userId: claims.userId,
      ...(claims.scope === undefined ? {} : { scope: claims.scope }),
      ...(claims.partnerId === undefined ? {} : { partnerId: claims.partnerId }),
      ...(claims.actAs === undefined ? {} : { actAs: claims.actAs }),
    };
  }
  private async tenantOf(accessToken: string): Promise<string> {
    return (await this.verify(accessToken)).tenantId;
  }

  requestPasswordResetCode(input: PasswordResetCodeRequestInput): Promise<PasswordResetCodeAccepted> {
    return this.deps.api.requestPasswordResetCode(input);
  }

  resendPasswordResetCode(input: PasswordResetCodeResendInput): Promise<PasswordResetCodeResent> {
    return this.deps.api.resendPasswordResetCode(input);
  }

  verifyPasswordResetCode(input: PasswordResetCodeVerifyInput): Promise<PasswordResetCodeVerified> {
    return this.deps.api.verifyPasswordResetCode(input);
  }

  confirmPasswordResetCode(input: PasswordResetCodeConfirmInput): Promise<void> {
    return this.deps.api.confirmPasswordResetCode(input);
  }
}
